import React, { useCallback, useEffect, useRef, useState } from "react";
import { ImageIcon, X } from "lucide-react";
import {
  ALLOWED_IMAGE_ACCEPT,
  ALLOWED_IMAGE_MIME_TYPES,
  MAX_IMAGE_FILE_SIZE_BYTES,
} from "components/common/imageUploadConstraints";
import { compressImageIfNeeded } from "components/common/utils/compressImage";
import "./ImageUploader.css";

const MAX_IMAGES = 3;

interface ImageUploaderProps {
  images: File[];
  onChange: (images: File[]) => void;
}

interface PreviewEntry {
  file: File;
  url: string;
}

const ImageUploader: React.FC<ImageUploaderProps> = ({ images, onChange }) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previews, setPreviews] = useState<PreviewEntry[]>([]);
  const previewsRef = useRef<PreviewEntry[]>([]);

  useEffect(() => {
    const next = images.map((file) => ({ file, url: URL.createObjectURL(file) }));
    previewsRef.current.forEach((p) => URL.revokeObjectURL(p.url));
    previewsRef.current = next;
    setPreviews(next);
    return () => {
      next.forEach((p) => URL.revokeObjectURL(p.url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images]);

  const addFiles = useCallback(
    async (fileList: FileList | File[]) => {
      setError(null);
      const incoming = Array.from(fileList);
      const remainingSlots = MAX_IMAGES - images.length;

      if (remainingSlots <= 0) {
        setError("You can attach up to 3 images.");
        return;
      }

      const toProcess = incoming.slice(0, remainingSlots);
      const accepted: File[] = [];

      for (const file of toProcess) {
        if (!ALLOWED_IMAGE_MIME_TYPES.includes(file.type)) {
          setError("Invalid file type. Please use JPEG, PNG, WEBP, or HEIC.");
          continue;
        }
        const processed = await compressImageIfNeeded(file);
        if (processed.size > MAX_IMAGE_FILE_SIZE_BYTES) {
          setError("One or more images exceed the 2MB limit.");
          continue;
        }
        accepted.push(processed);
      }

      if (accepted.length > 0) {
        onChange([...images, ...accepted]);
      }
    },
    [images, onChange],
  );

  const handleFileInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files) addFiles(event.target.files);
      if (fileInputRef.current) fileInputRef.current.value = "";
    },
    [addFiles],
  );

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setDragActive(false);
      if (event.dataTransfer.files?.length) addFiles(event.dataTransfer.files);
    },
    [addFiles],
  );

  const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
  }, []);

  const handleRemove = useCallback(
    (index: number) => {
      onChange(images.filter((_, i) => i !== index));
    },
    [images, onChange],
  );

  return (
    <div className="mrc-image-uploader">
      {previews.length > 0 && (
        <div className="mrc-image-uploader__thumbs">
          {previews.map((preview, index) => (
            <div className="mrc-image-uploader__thumb" key={preview.url}>
              <img src={preview.url} alt={`attachment ${index + 1}`} />
              <button
                type="button"
                className="mrc-image-uploader__remove"
                onClick={() => handleRemove(index)}
                aria-label="Remove image"
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}

      {images.length < MAX_IMAGES && (
        <div
          className={`mrc-image-uploader__dropzone${dragActive ? " mrc-image-uploader__dropzone--active" : ""}`}
          onClick={() => fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          role="button"
          tabIndex={0}
          aria-label="Add images"
        >
          <ImageIcon size={18} />
          <span>Add up to {MAX_IMAGES - images.length} image(s)</span>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={ALLOWED_IMAGE_ACCEPT}
        multiple
        className="mrc-image-uploader__input"
        onChange={handleFileInputChange}
        tabIndex={-1}
        aria-hidden="true"
      />

      {error && <p className="mrc-image-uploader__error">{error}</p>}
    </div>
  );
};

export default ImageUploader;
